import React, { useCallback, useEffect, useState } from "react";
import { marketplaceService, MarketplaceListingDetails } from "../../services/marketplaceService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faCheck, faCartShopping } from "@fortawesome/free-solid-svg-icons";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  LoadingState,
} from "../../components/ui";
import { formatMoney } from "../../utils/money";
import { ApiError } from "../../services/apiClient";

interface Props {
  listingId: number;
  onClose: () => void;
  onActivated: () => void;
}

const MarketplaceListingDetailsView: React.FC<Props> = ({ listingId, onClose, onActivated }) => {
  const [listing, setListing] = useState<MarketplaceListingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const toast = useToast();
  const isPaidListing = (listing?.priceCents ?? 0) > 0;
  const alreadyInLibrary = Boolean(listing?.activatedAt);

  const fetchDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await marketplaceService.getListingDetails(listingId);
      setListing(data);
    } catch (error) {
      console.error("Failed to fetch listing details:", error);
      toast.showToast({ type: "error", message: "Failed to load bundle details" });
    } finally {
      setLoading(false);
    }
  }, [listingId, toast]);

  useEffect(() => {
    void fetchDetails();
  }, [fetchDetails]);

  const handleActivate = async () => {
    try {
      setActivating(true);
      await marketplaceService.activateListing(listingId);
      toast.showToast({ type: "success", message: "Bundle added to your library successfully!" });
      onActivated();
      onClose();
    } catch (error) {
      // 409 is the double-tab case, not a failure: the bundle is already yours.
      if (error instanceof ApiError && error.status === 409) {
        toast.showToast({ type: "info", message: "This bundle is already in your library." });
        onActivated();
        onClose();
        return;
      }
      console.error("Failed to activate listing:", error);
      toast.showToast({ type: "error", message: "Failed to activate bundle" });
    } finally {
      setActivating(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faStore} className="text-amber-500" />
          <span className="font-serif tracking-widest uppercase">Bundle Details</span>
        </div>
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="py-20">
            <LoadingState message="Unrolling bundle contents..." size="md" />
          </div>
        ) : !listing ? (
          <div className="py-10 text-center text-zinc-500">Failed to load listing.</div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-1/3">
              <div className="relative aspect-square bg-zinc-800 rounded-xl overflow-hidden shadow-2xl border border-zinc-700 flex items-center justify-center">
                {listing.assets[0]?.imageUrl ? (
                  <img
                    src={listing.assets[0].imageUrl}
                    className="w-full h-full object-cover"
                    alt="Bundle Preview"
                  />
                ) : (
                  <FontAwesomeIcon icon={faStore} className="text-6xl text-zinc-700 opacity-20" />
                )}
              </div>

              <div className="mt-6 bg-zinc-800/50 rounded-xl p-4 border border-zinc-800">
                <h5 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">
                  What's Inside
                </h5>
                <ul className="space-y-3">
                  {listing.assets.map((asset) => (
                    <li key={asset.id} className="flex items-start gap-3 text-xs text-zinc-300">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <div>
                        <div className="font-bold text-zinc-100">{asset.name}</div>
                        <div className="text-zinc-500">
                          {asset.type} • {asset.category}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:w-2/3">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="font-serif text-3xl font-bold text-amber-500 mb-1 leading-tight">
                    {listing.title}
                  </h2>
                  <p className="text-zinc-500 text-sm">
                    Published by <span className="text-amber-400/80">Community Creator</span>
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold mb-1 text-white">
                    {listing.priceCents > 0
                      ? formatMoney(listing.priceCents, listing.currency)
                      : "Free"}
                  </div>
                  {listing.priceCents > 0 && (
                    <span className="bg-amber-500/10 text-amber-500 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest border border-amber-500/20 shadow-sm">
                      Full Ownership
                    </span>
                  )}
                </div>
              </div>

              <div className="border-y border-zinc-800 py-6 my-6 flex flex-wrap gap-8">
                <div>
                  <div className="text-xl font-bold text-white">{listing.assets.length}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-black mt-0.5">
                    Total Assets
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white">
                    {listing.assets.reduce((acc, a) => acc + a.sections.length, 0)}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-black mt-0.5">
                    Lore Sections
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-white">
                    {listing.assets.reduce((acc, a) => acc + a.diceTemplates.length, 0)}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-black mt-0.5">
                    Dice Templates
                  </div>
                </div>
              </div>

              <p className="text-zinc-400 leading-relaxed mb-8 text-sm">
                {listing.description ||
                  "Enhance your TTRPG experience with this premium marketplace bundle. Designed for immediate integration into your chronicles."}
              </p>

              <p className="text-[10px] text-zinc-600 uppercase tracking-widest border-t border-zinc-800 pt-4">
                Purchased assets are permanent and appear in your personal library with a{" "}
                <FontAwesomeIcon icon={faStore} className="text-amber-500 mx-1" /> icon.
              </p>
            </div>
          </div>
        )}
      </ModalBody>

      {!loading && listing && (
        <ModalFooter>
          <div className="flex gap-4 w-full">
            <Button
              variant="primary"
              onClick={handleActivate}
              loading={activating}
              disabled={isPaidListing || alreadyInLibrary}
              className="flex-1 font-bold h-12 rounded-xl active:scale-95 transition-transform"
              leftIcon={<FontAwesomeIcon icon={isPaidListing ? faCartShopping : faCheck} />}
            >
              {alreadyInLibrary
                ? "In your library"
                : isPaidListing
                  ? "Purchase Coming Soon"
                  : "Add to Library Free"}
            </Button>
            <Button variant="ghost" onClick={onClose} className="px-8 h-12 rounded-xl">
              Close
            </Button>
          </div>
          {isPaidListing && (
            <p className="mt-3 text-xs text-zinc-500">
              Paid bundle purchases are not available yet. Free bundles can still be added directly
              to your library.
            </p>
          )}
        </ModalFooter>
      )}
    </Modal>
  );
};

export default MarketplaceListingDetailsView;
